import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

// SERVICES
import { throwResponseAsLumResponseError } from 'v2/shared/api/lum-response-error';

import { ApiService } from '../../shared/services/api/api.service';

// UTILS

// INTERFACES
export interface TriggerEventRequest {
  model_type :string;
  model_id :number | string;
  event_name :string;
}

export interface TriggerEventResponse {
  run_id :string;
}

// Define LogContext based on the example, allow any extra properties
export interface LogContext {
  rule_id ?:number;
  condition_id ?:number;
  action_id ?:number;
  condition_config ?:Record<string, any>;
  action_config ?:Record<string, any>;
  result ?:boolean;
  error_details ?:string;
  [key :string] :any; // Allow other properties
}

export interface LogEntry {
  id :number;
  timestamp :string; // ISO 8601 format string
  level :'DEBUG' | 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
  description :string;
  context ?:LogContext;
}

//

@Injectable({
  providedIn: 'root', // Assuming it should be singleton
})
export class AutomationRuleApiService {
  private readonly basePath = '/v3/api/admin/automation'; // Specific path for these automation endpoints

  constructor(
    private readonly apiService :ApiService,
    // EnvironmentUtilService is likely already used by ApiService internally, so we might not need it here
    // private readonly environmentUtilService: EnvironmentUtilService
  ) {}

  /**
   * Triggers an automation event.
   * @param data The details for the event trigger.
   * @returns Observable containing the run ID.
   */
  triggerEvent(data :TriggerEventRequest) :Observable<TriggerEventResponse> {
    // Append the log_level parameter to the path
    const path = `${this.basePath}/trigger-event?log_level=debug`;

    // Use apiService.post, passing the path and body
    return this.apiService.post({ path, body: data }).pipe(
      // Error handling might be slightly different depending on ApiService, adjust if needed
      catchError(
        throwResponseAsLumResponseError('Failed to trigger automation event'),
      ),
    );
  }

  /**
   * Fetches logs for a specific automation run.
   * @param runId The UUID of the automation run.
   * @param lastFetchTime Optional timestamp to get logs after this time.
   * @param limit Optional limit for the number of log entries.
   * @returns Observable array of log entries.
   */
  getLogs(
    runId :string,
    lastFetchTime ?:string,
    limit ?:number,
  ) :Observable<LogEntry[]> {
    const path = `${this.basePath}/runs/${runId}/logs`;
    const params :Record<string, string> = {}; // apiService.get might expect a plain object

    if (lastFetchTime) {
      params['last_fetch_time'] = lastFetchTime;
    }
    if (limit) {
      params['limit'] = limit.toString();
    }

    // Use apiService.get, passing the path and params object
    return this.apiService
      .get(path, { params })
      .pipe(
        catchError(
          throwResponseAsLumResponseError(`Failed to fetch logs for run ${runId}`),
        ),
      );
  }
}
